import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { AlertCircle, ChevronRight, ExternalLink, Lock, Loader2, Search, Pencil, Copy, Check, Eye, Code2, type LucideIcon } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { getContent, getContentList, saveContent, type ContentEntity } from '../../lib/api/content'
import { cn } from '../../lib/cn'
import { Stat } from '../conventions/ui'

type El = Record<string, unknown>

/** Language tabs are ordered like the legacy back-office; unknown codes fall to the end. */
const LANG_ORDER = ['it', 'en', 'de', 'fr']
const langRank = (l: string) => {
  const i = LANG_ORDER.indexOf(l)
  return i === -1 ? 99 : i
}

/**
 * The real merge tags the mailer substitutes at send time (NotificationService: `${key}` ->
 * $emailData[key]). Clicking one inserts it into the focused field; the copy icon copies it.
 */
export const EMAIL_TAGS: { tag: string; label: string }[] = [
  { tag: 'name', label: 'Nome cliente' },
  { tag: 'lastName', label: 'Cognome cliente' },
  { tag: 'email', label: 'Email cliente' },
  { tag: 'code', label: 'Codice prenotazione' },
  { tag: 'cancellationCode', label: 'Codice di cancellazione' },
  { tag: 'cancellationLink', label: 'Link di cancellazione' },
  { tag: 'arrivalDate', label: 'Data di arrivo' },
  { tag: 'departureDate', label: 'Data di partenza' },
  { tag: 'transportLabel', label: 'Tipo di veicolo' },
  { tag: 'total', label: 'Totale' },
  { tag: 'noConventionTotal', label: 'Totale senza convenzione' },
  { tag: 'convention', label: 'Convenzione' },
  { tag: 'services', label: 'Servizi' },
  { tag: 'paymentMode', label: 'Metodo di pagamento' },
  { tag: 'resetPasswordUrl', label: 'Link reset password' },
  { tag: 'qrCode', label: 'QR code (URL)' },
  { tag: 'siteBaseUrl', label: 'URL sito di prenotazione' },
  { tag: 'wwwBaseSiteUrl', label: 'URL sito principale' },
]

type Item = { id: string; name: string; code: string; langs: string[] }

function langsOf(row: ContentEntity): string[] {
  const t = (row as unknown as { translations?: unknown }).translations
  const arr = Array.isArray(t) ? (t as string[]).map((s) => String(s).toLowerCase()) : ['it']
  return [...new Set(arr)].sort((a, b) => langRank(a) - langRank(b))
}

function fmtDate(v: unknown): string {
  if (!v || typeof v !== 'string') return '—'
  try {
    return format(parseISO(v), 'dd/MM/yyyy')
  } catch {
    return String(v)
  }
}

/** Wrap recognised placeholders (`${x}`, plus legacy {{x}}/{x}/!x) so they stand out in the preview. */
function highlightTokens(html: string): string {
  return html.replace(/(\$\{[\w.]+\}|\{\{\s*[\w.]+\s*\}\}|\{[\w.]+\}|![a-zA-Z][\w]*)/g, '<mark class="tok">$1</mark>')
}

function buildSrcDoc(html: string): string {
  return `<!doctype html><html lang="it"><head><meta charset="utf-8"><base target="_blank"><style>
    :root{color-scheme:light}
    body{margin:0;padding:24px;font-family:Assistant,system-ui,-apple-system,'Segoe UI',sans-serif;font-size:14px;line-height:1.65;color:#18181b;background:#fff}
    img{max-width:100%;height:auto}
    a{color:#E1021B}
    p{margin:0 0 12px}
    table{max-width:100%;border-collapse:collapse}
    h1,h2,h3{color:#18181b}
    .rteright{text-align:right}
    mark.tok{background:#fdecee;color:#b3010f;padding:0 3px;border-radius:3px;font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:.85em;font-weight:600}
  </style></head><body>${highlightTokens(html || '<p style="color:#a1a1aa">Nessun contenuto.</p>')}</body></html>`
}

export function TemplateLibrary({
  model,
  title,
  description,
  icon: Icon,
  showSubject,
  readOnly,
  unit,
  unitPlural,
  searchPlaceholder,
}: {
  model: 'email' | 'pdf'
  title: string
  description: ReactNode
  icon: LucideIcon
  showSubject: boolean
  readOnly?: boolean
  unit: string
  unitPlural: string
  searchPlaceholder: string
}) {
  const qc = useQueryClient()
  const editable = !readOnly
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [lang, setLang] = useState('it')
  const [q, setQ] = useState('')

  // edit state
  const [editing, setEditing] = useState(false)
  const [showCode, setShowCode] = useState(true) // edit view: code (textarea) vs live preview
  const [draft, setDraft] = useState<{ subject: string; body: string }>({ subject: '', body: '' })
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const lastFocused = useRef<'subject' | 'body'>('body')
  const subjectRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const list = useQuery({
    queryKey: ['content', model, 'all'],
    queryFn: () => getContentList(model, { limit: 300 }),
  })

  const items = useMemo<Item[]>(() => {
    const rows = list.data?.data ?? []
    return rows
      .map((r) => {
        const el = r.elements as El
        return {
          id: r.id,
          name: String(el.name ?? 'Senza nome'),
          code: String(el.code ?? ''),
          langs: langsOf(r),
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'it'))
  }, [list.data])

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase()
    if (!n) return items
    return items.filter((i) => i.name.toLowerCase().includes(n) || i.code.toLowerCase().includes(n))
  }, [items, q])

  const selected = items.find((i) => i.id === selectedId) ?? null

  // Auto-select the first (filtered) item, and keep selection valid as the filter changes.
  useEffect(() => {
    if (items.length === 0) return
    if (!selectedId || !items.some((i) => i.id === selectedId)) {
      setSelectedId((filtered[0] ?? items[0]).id)
    }
  }, [items, filtered, selectedId])

  // Clamp the chosen language to those the selected item actually has.
  useEffect(() => {
    if (selected && !selected.langs.includes(lang)) {
      setLang(selected.langs.includes('it') ? 'it' : selected.langs[0] ?? 'it')
    }
  }, [selected, lang])

  const detail = useQuery({
    queryKey: ['content', model, selectedId, lang],
    queryFn: () => getContent(model, selectedId!, lang),
    enabled: Boolean(selectedId),
    placeholderData: keepPreviousData,
  })

  // leaving an item/language exits edit mode; (re)seed the draft from whatever loads
  useEffect(() => {
    setEditing(false)
    setSaveError(null)
  }, [selectedId, lang])
  useEffect(() => {
    if (detail.data) {
      const e = detail.data.elements as El
      setDraft({ subject: String(e.subject ?? ''), body: String(e.text ?? '') })
    }
  }, [detail.data])

  const langCount = useMemo(() => new Set(items.flatMap((i) => i.langs)).size, [items])

  const el = (detail.data?.elements ?? {}) as El
  const subject = String(el.subject ?? '')
  const body = String(el.text ?? '')
  const showingRightId = detail.data?.id
  const fresh = showingRightId === selectedId
  const dirty = editing && (draft.subject !== subject || draft.body !== body)
  const previewBody = editing ? draft.body : body

  function insertTag(tag: string) {
    const token = '${' + tag + '}'
    const target = lastFocused.current
    const ref = target === 'subject' ? subjectRef.current : bodyRef.current
    if (!ref) {
      setDraft((d) => ({ ...d, body: d.body + token }))
      return
    }
    const start = ref.selectionStart ?? ref.value.length
    const end = ref.selectionEnd ?? ref.value.length
    const v = ref.value
    const nextVal = v.slice(0, start) + token + v.slice(end)
    setDraft((d) => (target === 'subject' ? { ...d, subject: nextVal } : { ...d, body: nextVal }))
    requestAnimationFrame(() => {
      ref.focus()
      const p = start + token.length
      ref.selectionStart = ref.selectionEnd = p
    })
  }
  function copyTag(tag: string) {
    const token = '${' + tag + '}'
    navigator.clipboard?.writeText(token).catch(() => {})
    setCopied(tag)
    window.setTimeout(() => setCopied((c) => (c === tag ? null : c)), 1200)
  }

  async function save() {
    if (!selected || !detail.data) return
    setSaving(true)
    setSaveError(null)
    try {
      const next: ContentEntity = {
        ...detail.data,
        elements: { ...(detail.data.elements as El), subject: draft.subject, text: draft.body },
      }
      await saveContent(model, next, lang)
      await qc.invalidateQueries({ queryKey: ['content', model] })
      setEditing(false)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Salvataggio non riuscito.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      {/* page-head */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <h2 className="font-display text-[26px] font-bold leading-tight tracking-[-.3px] text-coal sm:text-[28px]">
            {title}
          </h2>
          <p className="mt-1.5 max-w-[72ch] text-[13.5px] text-muted">{description}</p>
        </div>
        <a
          href={`/admin/yep-admin/content/${model}/list`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-lg border border-line bg-card px-4 py-[10px] text-[13.5px] font-semibold text-slate hover:border-faint sm:ml-auto"
        >
          <ExternalLink className="h-4 w-4" /> Gestionale
        </a>
      </div>

      {/* stat cards */}
      <div className="mb-[18px] grid grid-cols-2 gap-[14px] sm:grid-cols-3">
        <Stat label={unitPlural} value={String(items.length)} sub={`${unitPlural.toLowerCase()} configurati`} />
        <Stat label="Lingue" value={String(langCount)} sub="versioni tradotte" />
        <div className="cv-panel hidden items-center px-4 py-3.5 sm:flex">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold',
              readOnly ? 'bg-oro-soft text-oro-700' : 'bg-ok-soft text-ok',
            )}
          >
            {readOnly ? <Lock className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
            {readOnly ? 'Sola lettura' : 'Modificabile in gestionale'}
          </span>
        </div>
      </div>

      {list.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare i {unitPlural.toLowerCase()}.
        </div>
      ) : (
        <div className="grid grid-cols-1 items-start gap-[18px] xl:grid-cols-[minmax(280px,5fr)_7fr]">
          {/* ── left: list ── */}
          <div className="cv-panel flex max-h-[640px] flex-col overflow-hidden">
            <div className="border-b border-line-soft p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="h-9 w-full rounded-lg border border-line bg-card pl-9 pr-3 text-[13px] text-coal outline-none placeholder:text-faint focus:border-verdigris"
                />
              </div>
            </div>
            <div className="overflow-y-auto">
              {list.isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="border-b border-line-soft px-4 py-3">
                    <div className="skeleton h-3.5 w-40 rounded-sm bg-line-soft" />
                    <div className="skeleton mt-2 h-2.5 w-24 rounded-sm bg-line-soft" />
                  </div>
                ))
              ) : filtered.length === 0 ? (
                <div className="px-4 py-12 text-center text-[13px] text-muted">
                  {items.length === 0 ? `Nessun ${unit} presente.` : 'Nessun risultato.'}
                </div>
              ) : (
                filtered.map((i) => {
                  const active = i.id === selectedId
                  return (
                    <button
                      key={i.id}
                      onClick={() => setSelectedId(i.id)}
                      className={cn(
                        'flex w-full items-center gap-3 border-b border-line-soft px-4 py-3 text-left transition-colors last:border-0',
                        active ? 'bg-verdigris/[0.06]' : 'hover:bg-paper',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1',
                          active ? 'bg-verdigris/10 text-verdigris ring-verdigris/20' : 'bg-paper text-faint ring-line',
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-semibold text-coal">{i.name}</span>
                        <span className="mt-0.5 flex items-center gap-2">
                          {i.code && <span className="truncate font-mono text-[11px] text-faint">{i.code}</span>}
                          <span className="ml-auto shrink-0 font-mono text-[10px] font-semibold tracking-wide text-muted">
                            {i.langs.map((l) => l.toUpperCase()).join(' ')}
                          </span>
                        </span>
                      </span>
                      <ChevronRight className={cn('h-4 w-4 shrink-0', active ? 'text-verdigris' : 'text-faint')} />
                    </button>
                  )
                })
              )}
            </div>
          </div>

          {/* ── right: preview / editor ── */}
          <div className="cv-panel flex min-h-[560px] flex-col overflow-hidden">
            {!selected ? (
              <div className="flex flex-1 items-center justify-center text-sm text-muted">
                {list.isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : `Seleziona un ${unit}.`}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-start gap-3 border-b border-line-soft px-5 py-4">
                  <div className="min-w-0 flex-1">
                    {showSubject ? (
                      <>
                        <div className="text-[11px] font-bold uppercase tracking-[1.2px] text-faint">Oggetto</div>
                        {editing ? (
                          <input
                            ref={subjectRef}
                            value={draft.subject}
                            disabled={saving}
                            onFocus={() => (lastFocused.current = 'subject')}
                            onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                            placeholder="Oggetto dell’email"
                            className="mt-1 w-full rounded-md border border-line bg-card px-2.5 py-1.5 font-display text-[15px] font-bold text-coal outline-none focus:border-verdigris disabled:opacity-50"
                          />
                        ) : (
                          <div className="mt-0.5 truncate font-display text-[16px] font-bold text-coal" title={subject}>
                            {subject || selected.name}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="truncate font-display text-[16px] font-bold text-coal" title={selected.name}>
                        {selected.name}
                      </div>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted">
                      {selected.code && <span className="font-mono text-faint">{selected.code}</span>}
                      <span>Agg. {fmtDate(detail.data?.lastUpdate)}</span>
                      {dirty && <span className="font-semibold text-oro-700">• modifiche non salvate</span>}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* language switcher — only the languages this item actually has */}
                    {selected.langs.length > 1 && (
                      <div className="inline-flex overflow-hidden rounded-lg border border-line bg-card">
                        {selected.langs.map((l) => (
                          <button
                            key={l}
                            onClick={() => setLang(l)}
                            className={cn(
                              'px-3 py-1.5 font-mono text-[12px] font-semibold transition-colors',
                              lang === l ? 'bg-lagoon text-white' : 'text-muted hover:text-coal',
                            )}
                          >
                            {l.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    )}

                    {editable && !editing && (
                      <button
                        onClick={() => {
                          setEditing(true)
                          setShowCode(true)
                        }}
                        disabled={!detail.data}
                        className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Modifica
                      </button>
                    )}
                    {editable && editing && (
                      <>
                        <button
                          onClick={() => setShowCode((s) => !s)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-3 py-2 text-[13px] font-semibold text-slate hover:border-faint"
                        >
                          {showCode ? <Eye className="h-3.5 w-3.5" /> : <Code2 className="h-3.5 w-3.5" />}
                          {showCode ? 'Anteprima' : 'Codice'}
                        </button>
                        <button
                          onClick={() => {
                            setEditing(false)
                            if (detail.data) {
                              const e = detail.data.elements as El
                              setDraft({ subject: String(e.subject ?? ''), body: String(e.text ?? '') })
                            }
                          }}
                          disabled={saving}
                          className="rounded-lg border border-line bg-card px-3 py-2 text-[13px] font-semibold text-slate hover:border-faint disabled:opacity-50"
                        >
                          Annulla
                        </button>
                        <button
                          onClick={save}
                          disabled={saving || !dirty}
                          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                        >
                          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Salva
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* tag palette — only while editing the code */}
                {editable && editing && showCode && (
                  <div className="border-b border-line-soft bg-paper/60 px-5 py-3">
                    <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[1.2px] text-muted">
                      Variabili — clicca per inserire, o copia
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {EMAIL_TAGS.map((t) => (
                        <span key={t.tag} className="inline-flex items-center overflow-hidden rounded-md border border-line bg-card" title={t.label}>
                          <button
                            type="button"
                            onClick={() => insertTag(t.tag)}
                            className="px-2 py-1 font-mono text-[11px] font-semibold text-coal hover:bg-paper"
                          >
                            {'${' + t.tag + '}'}
                          </button>
                          <button
                            type="button"
                            onClick={() => copyTag(t.tag)}
                            title="Copia"
                            className="border-l border-line px-1.5 py-1 text-faint hover:bg-paper hover:text-coal"
                          >
                            {copied === t.tag ? <Check className="h-3 w-3 text-ok" /> : <Copy className="h-3 w-3" />}
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="relative flex-1 bg-card-2 p-4">
                  {detail.isError ? (
                    <div className="flex h-full items-center justify-center text-sm text-muted">
                      <AlertCircle className="mr-2 h-5 w-5 text-venetian" /> Anteprima non disponibile.
                    </div>
                  ) : editing && showCode ? (
                    <textarea
                      ref={bodyRef}
                      value={draft.body}
                      disabled={saving}
                      onFocus={() => (lastFocused.current = 'body')}
                      onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                      spellCheck={false}
                      className="h-full min-h-[460px] w-full resize-none rounded-lg border border-line bg-white p-3 font-mono text-[12.5px] leading-relaxed text-coal outline-none focus:border-verdigris disabled:opacity-50"
                    />
                  ) : (
                    <iframe
                      key={`${selectedId}-${lang}-${editing ? 'draft' : 'saved'}`}
                      title={`Anteprima ${selected.name}`}
                      sandbox=""
                      srcDoc={buildSrcDoc(previewBody)}
                      className={cn(
                        'h-full min-h-[460px] w-full rounded-lg border border-line bg-white transition-opacity',
                        fresh || editing ? 'opacity-100' : 'opacity-50',
                      )}
                    />
                  )}
                  {detail.isFetching && !editing && (
                    <span className="absolute right-6 top-6 inline-flex items-center gap-1.5 rounded-full bg-coal/80 px-2.5 py-1 text-[11px] font-semibold text-white">
                      <Loader2 className="h-3 w-3 animate-spin" /> {lang.toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 border-t border-line-soft px-5 py-2.5 text-[12px] text-muted">
                  {saveError ? (
                    <span className="font-medium text-venetian">{saveError}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-sm bg-verdigris/15 ring-1 ring-verdigris/30" />
                      Le aree evidenziate sono variabili sostituite all’invio.
                      {editing && ' Il corpo è HTML: modifica con cura.'}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
